import { supabase } from '../lib/supabase';

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

const checkInService = {
  // Create a new check-in record
  async createCheckIn(bookingId, renterId, ownerId) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.insert({
          booking_id: bookingId,
          renter_id: renterId,
          owner_id: ownerId,
          status: 'in_progress'
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Create check-in error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Create check-in error:', error);
      throw error;
    }
  },

  // Get check-in by booking ID
  async getCheckInByBookingId(bookingId) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.select('*')?.eq('booking_id', bookingId)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') {
          return { data: null, error: null };
        }
        console.log('Get check-in error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Get check-in error:', error);
      throw error;
    }
  },

  // Upload photo to storage
  async uploadPhoto(file, checkInId, section) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'User not authenticated' } };
      }

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${checkInId}/${section}/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { data, error } = await supabase?.storage?.from('renter-check-in-photos')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload photo error:', error?.message);
        return { data: null, error };
      }

      return { data: { path: filePath, fullPath: data?.path }, error: null };
    } catch (error) {
      console.error('Upload photo error:', error);
      return { data: null, error: { message: error?.message } };
    }
  },

  // Get signed URL for private photo
  async getPhotoUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage?.from('renter-check-in-photos')?.createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Get photo URL error:', error?.message);
        return { data: null, error };
      }

      return { data: data?.signedUrl, error: null };
    } catch (error) {
      console.error('Get photo URL error:', error);
      return { data: null, error: { message: error?.message } };
    }
  },

  // Update check-in section photos
  async updateSectionPhotos(checkInId, section, photos) {
    try {
      const columnMap = {
        exterior: 'exterior_photos',
        interior: 'interior_photos',
        hitch: 'hitch_photos',
        electrical: 'electrical_photos',
        tires: 'tire_photos'
      };

      const columnName = columnMap?.[section];
      if (!columnName) {
        return { data: null, error: { message: 'Invalid section name' } };
      }

      const { data, error } = await supabase?.from('renter_check_ins')?.update({ [columnName]: photos })?.eq('id', checkInId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Update section photos error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update section photos error:', error);
      throw error;
    }
  },

  // Update verification selfie
  async updateVerificationSelfie(checkInId, photoUrl) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.update({ verification_selfie_url: photoUrl })?.eq('id', checkInId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Update verification selfie error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update verification selfie error:', error);
      throw error;
    }
  },

  // Update damage notes
  async updateDamageNotes(checkInId, notes) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.update({ damage_notes: notes })?.eq('id', checkInId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Update damage notes error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update damage notes error:', error);
      throw error;
    }
  },

  // Update GPS location
  async updateGpsLocation(checkInId, latitude, longitude) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.update({
          gps_latitude: latitude,
          gps_longitude: longitude,
          gps_timestamp: new Date()?.toISOString()
        })?.eq('id', checkInId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Update GPS location error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update GPS location error:', error);
      throw error;
    }
  },

  // Complete check-in
  async completeCheckIn(checkInId) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.update({
          status: 'completed',
          completed_at: new Date()?.toISOString()
        })?.eq('id', checkInId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Complete check-in error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Complete check-in error:', error);
      throw error;
    }
  },

  // Submit check-in (sends notification to owner)
  async submitCheckIn(checkInId) {
    try {
      const { data, error } = await supabase?.from('renter_check_ins')?.update({
          status: 'submitted',
          submitted_at: new Date()?.toISOString()
        })?.eq('id', checkInId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Submit check-in error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Submit check-in error:', error);
      throw error;
    }
  },

  // Send notification to owner
  async notifyOwner(checkInId, ownerId, renterId, bookingId) {
    try {
      const { data, error } = await supabase?.from('messages')?.insert({
          booking_id: bookingId,
          sender_id: renterId,
          receiver_id: ownerId,
          content: `Renter has completed the pre-rental check-in inspection. Check-in ID: ${checkInId}. Please review the documentation.`
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Notify owner error:', error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Notify owner error:', error);
      throw error;
    }
  },

  // Delete photo from storage
  async deletePhoto(filePath) {
    try {
      const { error } = await supabase?.storage?.from('renter-check-in-photos')?.remove([filePath]);

      if (error) {
        console.error('Delete photo error:', error?.message);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Delete photo error:', error);
      return { error: { message: error?.message } };
    }
  }
};

export default checkInService;